/**
 * Logger Utility
 *
 * A structured logging system similar to Log4J for TypeScript.
 * Provides configurable log levels and environment-based filtering.
 *
 * Usage:
 *   import { logger } from './utils/logger';
 *
 *   logger.debug('User authentication started', { userId: '123' });
 *   logger.info('Profile loaded successfully');
 *   logger.warn('Retrying failed request', { attempt: 2 });
 *   logger.error('Firebase operation failed', { error });
 *
 * Configuration:
 *   - Production: Only WARN and ERROR messages are logged
 *   - Development/Preview: All log levels are logged (DEBUG, INFO, WARN, ERROR)
 */

import {
  LogLevel,
  getLogLevelName,
  type LogContext,
  type LogEntry,
  type LoggingConfig,
} from "../types/logging";

/**
 * Determine the minimum log level from environment variable
 */
function getMinLogLevel(): LogLevel {
  const configuredLevel = import.meta.env.VITE_MIN_LOG_LEVEL || "DEBUG";

  // Map string log level to numeric value
  switch (configuredLevel.toUpperCase()) {
    case "DEBUG":
      return LogLevel.DEBUG;
    case "INFO":
      return LogLevel.INFO;
    case "WARN":
    case "WARNING":
      return LogLevel.WARN;
    case "ERROR":
      return LogLevel.ERROR;
    case "SILENT":
      return LogLevel.SILENT;
    default:
      return LogLevel.DEBUG;
  }
}

/**
 * Default logging configuration
 * Automatically adjusts based on environment
 */
const loggingConfig: LoggingConfig = {
  minLevel: getMinLogLevel(),
  includeTimestamp: true,
  includeLevel: true,
};

/**
 * Check if a given log level should be output
 */
function shouldLog(level: LogLevel): boolean {
  const { minLevel } = loggingConfig;
  return level >= minLevel;
}

/**
 * Format a log entry for output
 */
function formatLogEntry(entry: LogEntry): string {
  const parts: string[] = [];

  // Timestamp
  const timestamp = entry.timestamp.toISOString();
  parts.push(`[${timestamp}]`);

  // Log level - convert numeric enum to string name
  parts.push(`[${getLogLevelName(entry.level)}]`);

  // Message
  parts.push(entry.message);

  return parts.join(" ");
}

/**
 * Output a log entry to the appropriate console method
 */
function outputLog(entry: LogEntry, context?: LogContext): void {
  if (!shouldLog(entry.level)) {
    return;
  }

  const formattedMessage = formatLogEntry(entry);

  switch (entry.level) {
    case LogLevel.DEBUG:
      if (context) {
        console.debug(formattedMessage, context);
      } else {
        console.debug(formattedMessage);
      }
      break;
    case LogLevel.INFO:
      if (context) {
        console.info(formattedMessage, context);
      } else {
        console.info(formattedMessage);
      }
      break;
    case LogLevel.WARN:
      if (context) {
        console.warn(formattedMessage, context);
      } else {
        console.warn(formattedMessage);
      }
      break;
    case LogLevel.ERROR:
      if (context) {
        console.error(formattedMessage, context);
      } else {
        console.error(formattedMessage);
      }
      break;
    case LogLevel.SILENT:
      // SILENT level produces no output
      break;
  }
}

/**
 * Core logging function
 */
function log(level: LogLevel, message: string, context?: LogContext): void {
  const entry: LogEntry = {
    level,
    message,
    context,
    timestamp: new Date(),
  };

  outputLog(entry, context);
}

/**
 * Logger instance with convenience methods
 */
export const logger = {
  /**
   * Log a debug message
   * Use for detailed diagnostic information
   */
  debug(message: string, context?: LogContext): void {
    log(LogLevel.DEBUG, message, context);
  },

  /**
   * Log an informational message
   * Use for general informational messages
   */
  info(message: string, context?: LogContext): void {
    log(LogLevel.INFO, message, context);
  },

  /**
   * Log a warning message
   * Use for potentially harmful situations
   */
  warn(message: string, context?: LogContext): void {
    log(LogLevel.WARN, message, context);
  },

  /**
   * Log an error message
   * Use for error events that might still allow the application to continue
   */
  error(message: string, context?: LogContext): void {
    log(LogLevel.ERROR, message, context);
  },
};

/**
 * Create a child logger with a specific context prefix
 * Useful for adding module/component context to all logs
 *
 * @example
 * const authLogger = createLogger({ prefix: 'AuthContext' });
 * authLogger.debug('Starting sign-in'); // [DEBUG] [AuthContext] Starting sign-in
 */
export function createLogger(params: { prefix: string }) {
  const { prefix } = params;
  return {
    debug(message: string, context?: LogContext): void {
      logger.debug(`[${prefix}] ${message}`, context);
    },
    info(message: string, context?: LogContext): void {
      logger.info(`[${prefix}] ${message}`, context);
    },
    warn(message: string, context?: LogContext): void {
      logger.warn(`[${prefix}] ${message}`, context);
    },
    error(message: string, context?: LogContext): void {
      logger.error(`[${prefix}] ${message}`, context);
    },
  };
}
